/**
 * Every store has its own session in this admin (adr/0033), and a tab shows the store named at
 * the start of its address: `/s/<tenantId>/…`. Signing in to another store, or leaving one, then
 * touches only that store's tabs. A tab without the prefix has no store yet (sign-in, invitation,
 * the platform's entry link) and moves to its store's address once it has one.
 */
const STORE_PREFIX = /^\/s\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/;

/** The store named by an address path, if any. */
export function storeOfPath(pathname: string): string | undefined {
  return STORE_PREFIX.exec(pathname)?.[1];
}

/** The address of a screen of a store (`path` is the screen's own path, `/` for the first). */
export function storePath(tenantId: string, path = '/'): string {
  return `/s/${tenantId}${path.startsWith('/') ? path : `/${path}`}`;
}

/** The store this tab shows, read once: a tab never changes stores without loading again. */
export const tabStore: string | undefined =
  typeof window === 'undefined' ? undefined : storeOfPath(window.location.pathname);

/** The router's base: the store's prefix, or the root for a tab without a store. */
export const basepath = tabStore === undefined ? '/' : `/s/${tabStore}`;

/** What the API is told about this tab's session: its store, or `none` (adr/0033). */
export function storeSessionHeader(store: string | undefined): string {
  return store ?? 'none';
}

/** An address of this tab as the browser shows it (the base included). */
export function tabHref(path: string): string {
  return tabStore === undefined ? path : storePath(tabStore, path);
}

/** Opens a store's screen in this tab: a store is another base, so the page loads again. */
export function goToStore(tenantId: string, path = '/', replace = false): void {
  const href = storePath(tenantId, path);
  if (replace) window.location.replace(href);
  else window.location.assign(href);
}
